"use client";

import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import { buttonClass } from "@/components/ui/button";
import { requestSendToLaptop } from "@/lib/extension/send-to-laptop-client";

type SendState = "idle" | "sending" | "sent" | "signin" | "error";

/**
 * "Kirim ke laptop": the phone-side half of the extension install story. The
 * extension only runs in desktop Chrome, Edge or Brave, so on a phone we email
 * the signed-in seeker the store link plus the page they are on. Guests get a
 * sign-in link that returns them here.
 *
 * Telemetry: `extension_send_to_laptop_clicked {surface}` and
 * `extension_send_to_laptop_result {surface, outcome}`.
 */
export function SendToLaptopButton({
	locale,
	surface,
	variant = "secondary",
	jobId,
	draft,
}: {
	locale: string;
	/** Where the button sits, for telemetry (e.g. "apply_pack"). */
	surface: string;
	variant?: "primary" | "secondary";
	/** Job being applied to: the email carries its link and is limited to one per day. */
	jobId?: string;
	/** Prepared application email to carry to the laptop. */
	draft?: { to: string; subject: string; body: string };
}) {
	const posthog = usePostHog();
	const [state, setState] = useState<SendState>("idle");
	const isEn = locale === "en";

	async function send() {
		posthog?.capture("extension_send_to_laptop_clicked", {
			locale,
			surface,
			job_id: jobId ?? null,
			has_draft: Boolean(draft),
		});
		setState("sending");
		const outcome: SendState = await requestSendToLaptop(locale, {
			jobId,
			draft,
		});
		setState(outcome);
		posthog?.capture("extension_send_to_laptop_result", {
			locale,
			surface,
			outcome,
			job_id: jobId ?? null,
			has_draft: Boolean(draft),
		});
	}

	if (state === "sent") {
		return (
			<p
				role="status"
				className="text-xs text-muted-foreground"
				data-testid="send-to-laptop-sent"
			>
				{isEn
					? "Sent. Open the email on your laptop to install the extension."
					: "Terkirim. Buka email-nya di laptop untuk memasang ekstensi."}
			</p>
		);
	}

	if (state === "signin") {
		const here = `${window.location.pathname}${window.location.search}`;
		return (
			<p role="status" className="text-xs text-muted-foreground">
				{isEn
					? "Sign in so we know where to send it. "
					: "Masuk dulu supaya kami tahu ke mana mengirimnya. "}
				<a
					href={`/${locale}/login?callbackURL=${encodeURIComponent(here)}`}
					className="font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
				>
					{isEn ? "Sign in" : "Masuk"}
				</a>
			</p>
		);
	}

	return (
		<div className="flex flex-col gap-1">
			<button
				type="button"
				onClick={send}
				disabled={state === "sending"}
				data-testid="send-to-laptop"
				className={buttonClass({ size: "sm", variant })}
			>
				{state === "sending"
					? isEn
						? "Sending…"
						: "Mengirim…"
					: draft
						? isEn
							? "Send this draft to my laptop"
							: "Kirim draf ini ke laptop"
						: isEn
							? "Send to my laptop"
							: "Kirim ke laptop"}
			</button>
			{state === "error" ? (
				<p role="alert" className="text-xs text-muted-foreground">
					{isEn ? "Could not send. Try again." : "Gagal mengirim. Coba lagi."}
				</p>
			) : null}
		</div>
	);
}
